import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import Loader from "../../components/Loader";
import BaseLayout from "../../layouts/BaseLayout";
import styles from "./styles.module.scss";
import {IDateFilter} from "../AutomaticAssessmentPage";
import CustomDatePicker, {TDatePickerItem} from "../../components/CustomDatePicker";
import {useSearchParams} from "react-router-dom";
import DateService from "../../services/DateService";
import {EnumDateFastFilterType, initialDateFilters} from "../../constants/common";
import DateFastFilter from "../AutomaticAssessmentPage/components/DateFastFilter";
import CompanyObserver from "../../mobx/company/CompanyObserver";
import FetchService from "../../services/api/FetchService";
import ChecklistSwitch, {IChecklistSwitch} from "../ChecklistPage/components/ChecklistSwitch";
import {PerformanceByIndicatorsResource, TotalScoreByEmployeeResource} from "../../resources/dashboard.resource";
import PerformanceByIndicatorsBarChart from "./performance_by_indicators_bar_chart";
import {Tab, Tabs} from "@mui/material";
import TotalScoreByEmployeesBarChart from "./total_score_by_employees_bar_chart";
import {ICallChecklistResource} from "../../resources/ICallChecklist";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanelContent(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dashboard-tab-panel-${index}`}
            aria-labelledby={`dashboard-tab-${index}`}
            {...other}
        >
            {value === index &&
                <div className={styles.dashboard_chart__container}>
                    <div className={styles.dashboard_chart__content}>
                        {children}
                    </div>
                </div>
            }
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `dashboard-tab-${index}`,
        'aria-controls': `dashboard-tab-panel-${index}`,
    };
}

const DashboardPage = () => {
    const [isLoadingChecklists, setIsLoadingChecklists] = useState(false);
    const [isLoadingPerformanceByIndicators, setIsLoadingPerformanceByIndicators] = useState(false);
    const [isLoadingTotalScoreByEmployee, setIsLoadingTotalScoreByEmployee] = useState(false);
    const [tab, setTab] = React.useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [checklist, setChecklist] = useState<ICallChecklistResource[]>([]);
    const [allChecklists, setAllChecklists] = useState<IChecklistSwitch[]>([]);
    const [performanceByIndicators, setPerformanceByIndicators] = useState<PerformanceByIndicatorsResource[]>([]);
    const [totalScoreByEmployee, setTotalScoreByEmployee] = useState<TotalScoreByEmployeeResource[]>([]);

    const isLoading = isLoadingChecklists || isLoadingPerformanceByIndicators || isLoadingTotalScoreByEmployee;

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const loadChecklists = async () => {
        if (isLoadingChecklists || allChecklists.length !== 0) return;

        const companyId = CompanyObserver.getCompanyId();
        if (!companyId) return;

        try {
            setIsLoadingChecklists(true);

            const checklists = await FetchService.getAllChecklists(companyId);
            setAllChecklists(checklists.data.map((item, index) => ({
                ...item,
                isSelected: index === 0,// first element selected
            })));
        } finally {
            setIsLoadingChecklists(false);
        }
    }

    const loadPerformanceByIndicators = async () => {
        if (isLoadingPerformanceByIndicators) return;

        const companyId = CompanyObserver.getCompanyId();
        if (!companyId) return;
        const checklistId = allChecklists.find((item) => item.isSelected)?.id ?? 0;

        try {
            setIsLoadingPerformanceByIndicators(true);
            const data =
                await FetchService.performanceByIndicators(companyId, checklistId, paramsFactory());
            setPerformanceByIndicators(data.data);
        } finally {
            setIsLoadingPerformanceByIndicators(false);
        }
    }

    const loadTotalScoreByEmployee = async () => {
        if (isLoadingTotalScoreByEmployee) return;

        const companyId = CompanyObserver.getCompanyId();
        if (!companyId) return;
        const checklistId = allChecklists.find((item) => item.isSelected)?.id ?? 0;

        try {
            setIsLoadingTotalScoreByEmployee(true);
            const data =
                await FetchService.totalScoreByEmployee(companyId, checklistId, paramsFactory());
            setTotalScoreByEmployee(data.data);
        } finally {
            setIsLoadingTotalScoreByEmployee(false);
        }
    }

    const paramsFactory = () => {
        const startDate = DateService.toIsoDateTime(getSearchParams('startDate'));
        const endDate = DateService.toIsoDateTime(getSearchParams('endDate'));
        if (!startDate) return;

        const params = new URLSearchParams();
        params.set('start', startDate.toISOString());
        params.set('end', endDate.toISOString());
        return params;
    };

    useEffect(() => {
        if (!getSearchParams('dateFilterType')) {
            setSearchParams((prev) => {
                const {start, end} = DateService.getLast30Days();
                prev.set('dateFilterType', EnumDateFastFilterType.LAST_30_DAYS);
                prev.set('startDate', start);
                prev.set('endDate', end);
                return prev;
            });
        }
        loadChecklists();
        loadPerformanceByIndicators();
        loadTotalScoreByEmployee();
    }, []);

    useEffect(() => {
        if (!getSearchParams('dateFilterType')) {
            setSearchParams((prev) => {
                prev.set('dateFilterType', EnumDateFastFilterType.LAST_30_DAYS);
                prev.set('startDate', DateService.getLast30Days().start);
                prev.set('endDate', DateService.getLast30Days().end);
                return prev;
            });
        }
        loadPerformanceByIndicators();
        loadTotalScoreByEmployee();
    }, [searchParams, allChecklists]);

    useEffect(() => {
        if (!getSearchParams('dateFilterType')) {
            setSearchParams((prev) => {
                const {start, end} = DateService.getLast30Days();
                prev.set('dateFilterType', EnumDateFastFilterType.LAST_30_DAYS);
                prev.set('startDate', start);
                prev.set('endDate', end);
                return prev;
            });
        }
        loadChecklists();
        loadPerformanceByIndicators();
        loadTotalScoreByEmployee()
    }, [CompanyObserver.companyId]);

    const getSearchParams = (key: string) => searchParams.get(key);

    const handleSelectFastDateFilter = (start: string, end: string, type: EnumDateFastFilterType) => {
        setSearchParams((prev) => {
            prev.set('dateFilterType', type);
            prev.set('startDate', start);
            prev.set('endDate', end);
            return prev;
        })
    }

    const getSearchParamsCustomRangeDate = (key: string) => {
        if (searchParams.get('dateFilterType') !== EnumDateFastFilterType.CUSTOM_RANGE) return null;
        const value = searchParams.get(key);
        if (!value || 'null' === value) return null;
        return new Date(value);
    }

    const handleSelectDate = (date: [TDatePickerItem, TDatePickerItem]) => {

        setSearchParams((prev) => {
            if (!date[0] && !date[1]) {
                prev.set('dateFilterType', EnumDateFastFilterType.TODAY);
                prev.set('startDate', DateService.formatUI(String(new Date()), 'YYYY-MM-DD'));
                prev.set('endDate', DateService.formatUI(String(new Date()), 'YYYY-MM-DD'));
                return prev;
            }

            prev.set('startDate', DateService.formatUI(String(date[0]), 'YYYY-MM-DD'));
            prev.set('endDate', date[1] ? DateService.formatUI(String(date[1]), 'YYYY-MM-DD') : 'null');
            prev.set('dateFilterType', EnumDateFastFilterType.CUSTOM_RANGE);
            return prev;
        });
    }

    return (
        <>
            {isLoading ? <Loader/> : null}
            <BaseLayout title={'Dashboard'}>
                <div className={styles.dashboard__header}>
                    <div className={styles.dashboard__dates}>
                        {initialDateFilters.map((item: IDateFilter) => {
                            if (item.type === EnumDateFastFilterType.CUSTOM_RANGE) return null;
                            return (
                                <DateFastFilter
                                    key={item.type}
                                    type={item.type}
                                    isActive={String(item.type) === getSearchParams('dateFilterType')}
                                    handleSetDate={handleSelectFastDateFilter}
                                />
                            )
                        })}
                        <CustomDatePicker
                            startDate={getSearchParamsCustomRangeDate('startDate')}
                            endDate={getSearchParamsCustomRangeDate('endDate')}
                            handleSelectDate={handleSelectDate}
                        />
                    </div>
                </div>
                <div className={styles.dashboard__subheader}>
                    <div/>
                    <ChecklistSwitch setChecklists={setAllChecklists} checklists={allChecklists}/>
                    <div/>
                </div>
                <Tabs value={tab} onChange={handleTabChange}>
                    <Tab label="Статистика виконнаня чек-ліста" {...a11yProps(0)} />
                    <Tab label="Аналіз дзвінків серед операторів" {...a11yProps(1)} />
                </Tabs>
                <TabPanelContent value={tab} index={0}>
                    <PerformanceByIndicatorsBarChart performanceByIndicators={performanceByIndicators}/>
                </TabPanelContent>
                <TabPanelContent value={tab} index={1}>
                    <TotalScoreByEmployeesBarChart totalScoreByEmployee={totalScoreByEmployee}/>
                </TabPanelContent>
            </BaseLayout>
        </>);
};

export default observer(DashboardPage);
